import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import LanguageToggleButton from './LanguageToggleButton';
import HeroSection from './HeroSection';
import { Sparkles, Brain, FlaskConical, Users, NotebookPen, ShieldCheck, BarChart3, Wind, Crown, Mic } from 'lucide-react';
import { BRAND_LOGO_URL } from '../data/brand';
import { HEROES } from '../data/heroes';
import { ACTIVITY_CATALOG } from '../data/activityCatalog';

const Landing = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('overview');

  const features = [
    { title: t('landing.feature1Title'), description: t('landing.feature1Desc'), Icon: NotebookPen, color: 'from-purple-500 to-fuchsia-500' },
    { title: t('landing.feature2Title'), description: t('landing.feature2Desc'), Icon: ShieldCheck, color: 'from-red-500 to-rose-500' },
    { title: t('landing.feature3Title'), description: t('landing.feature3Desc'), Icon: BarChart3, color: 'from-blue-500 to-cyan-500' },
    { title: t('landing.feature4Title'), description: t('landing.feature4Desc'), Icon: Wind, color: 'from-amber-400 to-yellow-500' }
  ];

  const heroCards = [
    { key: 'queen', Icon: Crown, color: 'text-amber-700', border: 'border-amber-200 hover:border-amber-400' },
    { key: 'victor', Icon: Mic, color: 'text-red-700', border: 'border-red-200 hover:border-red-400' },
    { key: 'cam', Icon: Wind, color: 'text-blue-700', border: 'border-blue-200 hover:border-blue-400' },
  ];

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-[#0a1a3e] via-[#1e3a8a] to-[#1d4ed8]">
      {/* Royal blue ambient blurs */}
      <div className="pointer-events-none absolute inset-0 opacity-30">
        <div className="absolute top-20 left-10 w-72 h-72 rounded-full bg-blue-500 blur-3xl" />
        <div className="absolute top-40 right-10 w-80 h-80 rounded-full bg-amber-400 blur-3xl" />
        <div className="absolute bottom-32 left-1/3 w-72 h-72 rounded-full bg-sky-400 blur-3xl" />
      </div>

      <div className="absolute top-4 right-4 z-20 hidden sm:block">
        <LanguageToggleButton />
      </div>
      <div className="absolute top-4 right-4 z-20 sm:hidden">
        <LanguageSwitcher />
      </div>

      {/* Hero */}
      <section className="relative z-10 pt-12 pb-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto text-center">
          {/* The Brain Superheroes hero logo */}
          <div className="mb-6 flex justify-center">
            <img
              src={BRAND_LOGO_URL}
              alt="The Brain Superheroes"
              className="w-full max-w-2xl drop-shadow-[0_15px_50px_rgba(251,191,36,0.4)]"
              data-testid="brand-hero-logo"
            />
          </div>

          <p className="text-amber-300 text-lg md:text-xl font-bold italic tracking-wide mb-4 flex items-center justify-center gap-2" data-testid="landing-title">
            <Sparkles className="w-5 h-5" /> {t('landing.tagline')} <Sparkles className="w-5 h-5" />
          </p>

          <p className="text-lg md:text-xl text-blue-50/90 max-w-3xl mx-auto leading-relaxed mb-2">
            {t('landing.subtitle')}
          </p>
          <p className="text-blue-100/80 max-w-3xl mx-auto leading-relaxed mb-10">
            <span className="text-amber-300">{t('landing.subtitleAccent')}</span> {t('landing.subtitleEnd')}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button
              onClick={() => navigate('/auth')}
              data-testid="get-started-btn"
              className="px-8 py-6 text-base font-bold rounded-full bg-gradient-to-r from-amber-400 to-yellow-500 text-blue-950 hover:from-amber-300 hover:to-yellow-400 shadow-[0_10px_40px_-10px_rgba(251,191,36,0.7)]"
            >
              {t('landing.cta')}
            </Button>
            <Button
              onClick={() => setActiveTab('about')}
              variant="outline"
              data-testid="learn-more-btn"
              className="px-8 py-6 text-base font-semibold rounded-full bg-white/10 backdrop-blur border-white/30 text-white hover:bg-white/20"
            >
              {t('landing.learnMore')}
            </Button>
          </div>

          {/* Prominent language toggle directly below CTAs */}
          <div className="mt-6 flex justify-center">
            <LanguageToggleButton />
          </div>

          <div className="flex flex-wrap justify-center items-center gap-4 mt-10 text-sm text-blue-100/90">
            <div className="flex items-center gap-2 bg-white/5 backdrop-blur rounded-full px-4 py-2 border border-white/10">
              <Brain className="w-4 h-4 text-amber-300" /><span>{t('landing.trust1')}</span>
            </div>
            <div className="flex items-center gap-2 bg-white/5 backdrop-blur rounded-full px-4 py-2 border border-white/10">
              <FlaskConical className="w-4 h-4 text-amber-300" /><span>{t('landing.trust2')}</span>
            </div>
            <div className="flex items-center gap-2 bg-white/5 backdrop-blur rounded-full px-4 py-2 border border-white/10">
              <Users className="w-4 h-4 text-amber-300" /><span>{t('landing.trust3')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Hero (character) section on dark bg with white card backdrop */}
      <section className="relative z-10 bg-gradient-to-b from-transparent via-white/95 to-white pt-4">
        <HeroSection variant="landing" />
      </section>

      {/* Tabs */}
      <section className="relative z-10 bg-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-8">
              <TabsTrigger value="overview" className="text-base font-semibold" data-testid="overview-tab">{t('landing.overview')}</TabsTrigger>
              <TabsTrigger value="features" className="text-base font-semibold" data-testid="features-tab">{t('landing.features')}</TabsTrigger>
              <TabsTrigger value="about" className="text-base font-semibold" data-testid="about-tab">{t('landing.aboutUs')}</TabsTrigger>
            </TabsList>

            <TabsContent value="overview">
              <div className="text-center py-12">
                <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4">{t('landing.welcomeHeader')}</h2>
                <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed mb-10">{t('landing.welcomeBody')}</p>
                <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                  {heroCards.map(({ key, Icon, color, border }) => {
                    const hero = HEROES[key];
                    const tr = t(`heroes.${key}`, { returnObjects: true });
                    const count = ACTIVITY_CATALOG[key].length;
                    return (
                      <Card key={key} className={`p-6 text-center border-2 ${border} transition-all hover:-translate-y-1`} data-testid={`landing-hero-card-${key}`}>
                        <div className={`hero-avatar w-24 h-28 mx-auto mb-3 ${hero.imageBg}`}>
                          <img src={hero.image} alt={tr.name} />
                        </div>
                        <div className={`flex items-center justify-center gap-1 mb-1 ${color}`}>
                          <Icon className="w-4 h-4" />
                          <span className="text-xs font-bold uppercase tracking-widest">{tr.pathTitle}</span>
                        </div>
                        <h3 className="text-lg font-extrabold text-gray-900 mb-1">{tr.name}</h3>
                        <p className="text-sm text-gray-600 mb-3">{tr.pathDesc}</p>
                        <span className="inline-flex items-center gap-1 text-xs font-bold text-blue-700 bg-blue-50 px-3 py-1 rounded-full">
                          <Sparkles className="w-3 h-3" />
                          {t('heroLed.activitiesCount', { n: count })}
                        </span>
                      </Card>
                    );
                  })}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="features">
              <div className="py-8">
                <div className="text-center mb-10">
                  <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-3">{t('landing.featuresTitle')}</h2>
                  <p className="text-lg text-gray-600 max-w-3xl mx-auto">{t('landing.featuresBody')}</p>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {features.map((feature, i) => (
                    <Card key={i} className="p-6 text-center hover:-translate-y-1 transition-transform" data-testid={`feature-card-${i}`}>
                      <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-r ${feature.color} flex items-center justify-center text-white`}>
                        <feature.Icon className="w-7 h-7" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{feature.title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{feature.description}</p>
                    </Card>
                  ))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="about">
              <div className="py-8 max-w-5xl mx-auto">
                <div className="text-center mb-10">
                  <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-2">About Brain Superheroes</h2>
                  <p className="text-lg text-blue-700 font-semibold">Supporting Youth Mental Health & Life Readiness</p>
                </div>
                <div className="grid md:grid-cols-2 gap-8">
                  <Card className="p-6">
                    <h3 className="text-xl font-bold mb-3 flex items-center gap-2"><Sparkles className="w-5 h-5 text-amber-500" /> Our Mission</h3>
                    <p className="text-gray-700 leading-relaxed text-sm">The Brain Superheroes Wellness App addresses the critical needs of youth — mental health, life readiness, and suicide prevention — by giving them relatable heroes (The Self-Esteem Queen, Victor the Voice, and Coping Cam) and the tools to be their own heroes.</p>
                  </Card>
                  <Card className="p-6">
                    <h3 className="text-xl font-bold mb-3 flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-blue-600" /> Why It Matters</h3>
                    <p className="text-gray-700 leading-relaxed text-sm">Suicide has been the second leading cause of death among those aged 10-19 since 2017, with rates among Black youth rising faster than any other group. The Brain Superheroes books and this app work together to give youth the tools, language, and confidence to be their own heroes.</p>
                  </Card>
                </div>
                <div className="text-center mt-10 flex flex-col sm:flex-row gap-3 justify-center">
                  <a href="https://books.by/the-brain-superheroes" target="_blank" rel="noopener noreferrer" className="px-6 py-3 rounded-full bg-amber-400 text-blue-950 font-bold hover:bg-amber-300 transition-colors inline-flex items-center gap-2 justify-center">
                    <NotebookPen className="w-4 h-4" /> Get the Books
                  </a>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="relative z-10 py-16 bg-gradient-to-br from-blue-700 via-sky-600 to-amber-500 text-white text-center px-4">
        <h2 className="text-3xl md:text-5xl font-extrabold mb-4">{t('landing.bottomCta')}</h2>
        <p className="text-blue-50 max-w-2xl mx-auto mb-8 text-lg">{t('landing.bottomCtaBody')}</p>
        <Button
          onClick={() => navigate('/auth')}
          data-testid="bottom-cta-btn"
          className="px-10 py-6 text-base font-bold rounded-full bg-gradient-to-r from-amber-400 to-yellow-500 text-blue-950 hover:from-amber-300 hover:to-yellow-400 inline-flex items-center gap-2"
        >
          {t('landing.getStarted')} <Sparkles className="w-4 h-4" />
        </Button>
      </section>

      {/* Footer */}
      <footer className="relative z-10 bg-[#071233] text-blue-200 py-8 text-center text-sm" data-testid="landing-footer">
        <div className="flex items-center justify-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-full overflow-hidden bg-black/40">
            <img src={BRAND_LOGO_URL} alt="" className="w-full h-full object-cover" />
          </div>
          <span className="font-bold text-white">{t('brand.name')}</span>
        </div>
        <p className="text-blue-300/80">{t('brand.author')}</p>
        <p className="text-blue-400/60 text-xs mt-2">© 2026 The Brain Superheroes · All Rights Reserved</p>
        <div className="mt-4 flex justify-center">
          <LanguageToggleButton />
        </div>
      </footer>
    </div>
  );
};

export default Landing;
